import React, { useEffect, useState } from "react";
import "./ResponsesPage.css";
import { DashboardSidebar } from "./Header";
import { getFirestore, collection, onSnapshot } from "firebase/firestore";
import { app } from "../firebase";
import { useNavigate } from "react-router-dom";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import Skeleton from "@mui/material/Skeleton";

function ResponsesPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseCounts, setResponseCounts] = useState({}); // { [formId]: count }
  const [shareFormId, setShareFormId] = useState(null);
  const navigate = useNavigate();

  const getFormUrl = (formId) =>
    `${window.location.origin}/form/${formId}/instructions/userform`;

  const handleShareClick = async (event, formId) => {
    event.stopPropagation();
    const formUrl = getFormUrl(formId);
    setShareFormId(formId);

    try {
      await navigator.clipboard.writeText(formUrl);
    } catch (error) {
      console.error("Unable to copy form link:", error);
    }
  };

  useEffect(() => {
    const db = getFirestore(app);
    const formRef = collection(db, "forms");
    const unsub = onSnapshot(formRef, (snapshot) => {
      const userEmail = sessionStorage.getItem("email");
      const userForms = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((f) => f.saveEmail === userEmail);
      setForms(userForms);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Response counts live under a separate top-level "responses" collection:
  // responses/{formId}/responses/{responseId} — not on the form doc itself.
  // Subscribe to each form's response subcollection to get a live count.
  const formIdsKey = forms.map((form) => form.id).sort().join(",");

  useEffect(() => {
    if (!formIdsKey) {
      setResponseCounts({});
      return;
    }

    const db = getFirestore(app);
    const ids = formIdsKey.split(",").filter(Boolean);

    const unsubscribers = ids.map((formId) => {
      const responsesRef = collection(db, "responses", formId, "responses");
      return onSnapshot(
        responsesRef,
        (snapshot) => {
          setResponseCounts((prev) => ({ ...prev, [formId]: snapshot.size }));
        },
        (error) => {
          console.error(`Error listening to responses for form ${formId}:`, error);
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [formIdsKey]);

  return (
    <div className="responses-page">
      <DashboardSidebar activePage="Responses" />
      <main className="responses-main">
        <div className="responses-header">
          <h2>Responses</h2>
          <p>View all responses collected across your forms.</p>
        </div>

        {loading ? (
          <div className="responses-grid">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={110} />
            ))}
          </div>
        ) : forms.length === 0 ? (
          <div className="responses-empty">
            <AssignmentOutlinedIcon sx={{ fontSize: 56, color: "#b0bec5" }} />
            <p>No forms found. Create a form to start collecting responses.</p>
          </div>
        ) : (
          <div className="responses-grid">
            {forms.map((form) => {
              const responseCount = responseCounts[form.id] || 0;
              return (
                <div
                  className="response-card"
                  key={form.id}
                  onClick={() => navigate(`/form/${form.id}/responses`)}
                >
                  <div className="response-card-icon">
                    <AssignmentOutlinedIcon />
                  </div>
                  <div className="response-card-body">
                    <h3>{form.formName || "Untitled Form"}</h3>
                    <p className="response-count-label">
                      <span className="count-badge">{responseCount}</span>
                      {responseCount === 1 ? " response" : " responses"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="response-card-share"
                    aria-label="Copy form link"
                    title="Copy form link"
                    onClick={(event) => handleShareClick(event, form.id)}
                  >
                    <OpenInNewRoundedIcon />
                  </button>
                  {shareFormId === form.id && (
                    <div
                      className="response-card-share-link"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span>Link copied</span>
                      <a
                        href={getFormUrl(form.id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open form
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default ResponsesPage;